import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { PaymentFilterCriteriaProps } from "pages/admin/Payments";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CancelCircleIcon,
  Payment02Icon,
  PaymentSuccess01Icon,
  TaskEdit01Icon,
  Upload01Icon,
  ViewIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import {
  CancelPaymentModal,
  EditPendingPaymentModal,
  MarkAsPaidModal,
  PayNowModal,
} from "./PaymentModules";
import { GET_PAYMENTS } from "graphql/payment";
import {
  canCancelPayment,
  canEditPayment,
  canMarkAsPaid,
  canPayNow,
  canResubmitPayment,
  formatPaymentForTable,
} from "utils/payments/paymentFormat";
import PaymentStatus from "enums/PaymentStatus";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";
import { RenderPaymentStatus } from "components/common/RenderPaymentStatus";
import { RenderPaymentMethod } from "components/common/payments/RenderPaymentMethod";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";

type PaymentsConnection = { edges?: { node: unknown }[] };

type Data = {
  id: string;
  dueDate: string;
  scheduledPaymentDate: string;
  actualPaymentDate: string;
  payTo: string;
  /** Already formatted for display, currency included. */
  amount: string;
  currency: string;
  payFrom: string;
  receivedFromName: string;
  receivedFromEmail: string;
  description: string;
  paymentMethod: string;
  status: PaymentStatus;
  workflowRequestStatus?: WorkflowRequestStatus;
};

type SendInvoicesTableProps = {
  filterCriteria: PaymentFilterCriteriaProps;
  refreshPayments: number;
  activeTab: number;
  paymentEmail?: string;
  handleOpenPaymentDetailsDrawer: (id: string) => void;
  handleOpenEditDetailsDrawer: (id: string) => void;
  handleRefreshPayments: () => void;
};

const PaymentsTable: React.FC<SendInvoicesTableProps> = (props) => {
  const {
    filterCriteria,
    refreshPayments,
    activeTab,
    paymentEmail,
    handleOpenPaymentDetailsDrawer,
    handleOpenEditDetailsDrawer,
    handleRefreshPayments,
  } = props;

  const { t } = useTranslation();

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );
  const [selectedPayment, setSelectedPayment] = useState<Data | null>(null);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [openEditPendingPaymentModal, setOpenEditPendingPaymentModal] =
    useState<boolean>(false);
  const [openPayNowModal, setOpenPayNowModal] = useState<boolean>(false);
  const [openMarkAsPaidModal, setOpenMarkAsPaidModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        status: filterCriteria.paymentStatus,
        searchTerm: filterCriteria.searchTerm,
        contactId: filterCriteria.contactId,
        sortDirection: "DESC",
      });
    }
  }, [filterCriteria]);

  const extractPayments = (
    data: Record<string, PaymentsConnection>,
  ): Data[] => {
    const firstKey = Object.keys(data)[0];
    return (
      data[firstKey]?.edges?.map((item) => formatPaymentForTable(item.node)) ??
      []
    );
  };

  const filterPaymentStatus = (item: Data) => {
    const statuses = filterCriteria.paymentStatus;
    if (!statuses || statuses.length === 0) return true;
    return statuses.includes(item.status);
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PAYMENTS,
      initialVariables: defaultPaginationVariables,
      extractData: extractPayments,
      filterFunction: filterPaymentStatus,
      refetchIndex: refreshPayments,
    });

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "receivedFromName",
        header: t("Sender"),
        cell: (payment) => (
          <Typography variant="subtitle2">
            {payment.receivedFromName || payment.receivedFromEmail}
          </Typography>
        ),
      },
      {
        id: "description",
        header: t("Description"),
        maxWidth: 200,
        cell: (payment) => payment.description,
      },
      {
        id: "dueDate",
        header: t("Due Date"),
        width: 0,
        cell: (payment) => ({
          content: payment.dueDate || "-",
          align: payment.dueDate ? "left" : "center",
        }),
      },
      {
        id: "scheduledPaymentDate",
        header: t("Payment Date"),
        width: 0,
        cell: (payment) => ({
          content: payment.scheduledPaymentDate || "-",
          align: payment.scheduledPaymentDate ? "left" : "center",
        }),
      },
      ...(activeTab === 2
        ? [
            {
              id: "actualPaymentDate",
              header: t("Paid Date"),
              width: 0,
              cell: (payment: Data) => ({
                content: payment.actualPaymentDate || "-",
                align: payment.actualPaymentDate
                  ? ("left" as const)
                  : ("center" as const),
              }),
            },
          ]
        : []),
      {
        id: "payTo",
        header: t("Pay To (Contact)"),
        cell: (payment) => ({
          content: payment.payTo || "-",
          align: payment.payTo ? "left" : "center",
        }),
      },
      {
        id: "amount",
        header: t("Amount"),
        align: "right",
        cell: (payment) => (
          <Typography variant="subtitle2">{payment.amount}</Typography>
        ),
      },
      {
        id: "paymentMethod",
        header: t("Payment Method"),
        width: 0,
        cell: (payment) => (
          <RenderPaymentMethod method={payment.paymentMethod} />
        ),
      },
      {
        id: "status",
        header: t("Status"),
        align: "center",
        cell: (payment) => (
          <RenderPaymentStatus
            paymentStatus={payment.status}
            workflowStatus={payment.workflowRequestStatus}
          />
        ),
      },
    ],
    [t, activeTab],
  );

  const rowActions = (payment: Data): RowAction[] => [
    {
      label: t("View Payment Details"),
      icon: <HugeiconsIcon icon={ViewIcon} size={16} />,
      onClick: () => handleOpenPaymentDetailsDrawer(payment.id),
    },
    ...(canEditPayment(payment.status) && !isAuditor
      ? [
          {
            label: t("Edit Payment Details"),
            icon: <HugeiconsIcon icon={TaskEdit01Icon} size={16} />,
            onClick: () => {
              setSelectedPayment(payment);
              if (payment.status === PaymentStatus.PENDING_PAYMENT) {
                setOpenEditPendingPaymentModal(true);
              } else {
                handleOpenEditDetailsDrawer(payment.id);
              }
            },
          },
        ]
      : []),
    ...(canResubmitPayment(payment.status, payment.actualPaymentDate) &&
    !isAuditor
      ? [
          {
            label: t("Resubmit Payment"),
            icon: <HugeiconsIcon icon={Upload01Icon} size={16} />,
            onClick: () => handleOpenEditDetailsDrawer(payment.id),
          },
        ]
      : []),
    ...(canPayNow(payment.status) && !isAuditor
      ? [
          {
            label: t("Pay Now"),
            icon: <HugeiconsIcon icon={Payment02Icon} size={16} />,
            onClick: () => {
              setSelectedPayment(payment);
              setOpenPayNowModal(true);
            },
          },
        ]
      : []),
    ...(canMarkAsPaid(payment.status) && !isAuditor
      ? [
          {
            label: t("Mark as Paid"),
            icon: <HugeiconsIcon icon={PaymentSuccess01Icon} size={16} />,
            onClick: () => {
              setSelectedPayment(payment);
              setOpenMarkAsPaidModal(true);
            },
          },
        ]
      : []),
    ...(canCancelPayment(payment.status) && !isAuditor
      ? [
          {
            label: t("Cancel Payment"),
            icon: <HugeiconsIcon icon={CancelCircleIcon} size={16} />,
            onClick: () => {
              setSelectedPayment(payment);
              setOpenCancelModal(true);
            },
          },
        ]
      : []),
  ];

  const emptyState =
    activeTab === 0 ? (
      <Stack
        gap="var(--space-4)"
        sx={{
          alignItems: "center",
          textAlign: "center",
          inlineSize: "100%",
          paddingBlock: "var(--space-8)",
        }}
      >
        <Typography variant="subtitle2">
          {t("EmptyPaymentInboxDescription")}
        </Typography>

        {paymentEmail && (
          <>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {t("EmptyPaymentEmailDescription")}
              <br />
              <Typography component="span" variant="subtitle2">
                {paymentEmail}
              </Typography>
            </Typography>

            <Link
              component={RouterLink}
              to="/admin/settings/company-settings?editMode=true"
              variant="body2"
            >
              {t("EditIntakeEmail")}
            </Link>
          </>
        )}
      </Stack>
    ) : (
      t("No payments found")
    );

  return (
    <Box sx={{ position: "relative", blockSize: "100%" }}>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(payment) => payment.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(payment) => handleOpenPaymentDetailsDrawer(payment.id)}
        onScroll={handleScroll}
        emptyState={emptyState}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more payments")}
        stickyHeader
      />

      {openCancelModal && (
        <CancelPaymentModal
          open={openCancelModal}
          setOpen={setOpenCancelModal}
          paymentId={selectedPayment?.id}
          handleUpdatePayments={handleRefreshPayments}
        />
      )}

      {openPayNowModal && (
        <PayNowModal
          open={openPayNowModal}
          setOpen={setOpenPayNowModal}
          paymentId={selectedPayment?.id}
          handleUpdatePayments={handleRefreshPayments}
        />
      )}

      {openMarkAsPaidModal && (
        <MarkAsPaidModal
          open={openMarkAsPaidModal}
          setOpen={setOpenMarkAsPaidModal}
          paymentId={selectedPayment?.id}
          handleUpdatePayments={handleRefreshPayments}
        />
      )}

      {openEditPendingPaymentModal && (
        <EditPendingPaymentModal
          open={openEditPendingPaymentModal}
          setOpen={setOpenEditPendingPaymentModal}
          handleYes={() => {
            selectedPayment && handleOpenEditDetailsDrawer(selectedPayment.id);
            setOpenEditPendingPaymentModal(false);
          }}
        />
      )}
    </Box>
  );
};
export default PaymentsTable;
